import ModalComponent from '@components/modal/Modal.component';
import {useTranslation} from 'react-i18next';
import {InvoiceDetails, invoicesService} from '@store/invoices/invoices';
import {Colors} from '@constants/colors.constants';
import {Button, Divider, Stack} from '@mui/material';
import {Sizes} from '@constants/sizes.constants';
import {map} from 'rxjs';
import React, {useEffect, useState} from 'react';
import {clientDetailsService, ClientInvoiceAddress} from '@store/clients/details';
import AsyncSelectComponent from '@components/inputs/asyncSelect/AsyncSelect.component';
import {SelectItem} from '@components/inputs/select/Select.component';
import TextFieldComponent from '@components/inputs/textField/TextField.component';
import {LoadingButton} from '@mui/lab';
import {useSnackbar} from 'notistack';

interface UpdateBillAddressModalProps {
  handleClose?: (invoiceDetails?: InvoiceDetails) => void;
  invoiceDetails?: InvoiceDetails;
}


export const UpdateBillAddressModal = (props: UpdateBillAddressModalProps) => {
  const {handleClose, invoiceDetails} = props;
  const {t} = useTranslation();

  const [address, setAddress] = useState<ClientInvoiceAddress & { name: string; addressItem?: SelectItem }>({name: ''});

  const {enqueueSnackbar} = useSnackbar();

  useEffect(() => {
    const streetComponents = invoiceDetails?.wireToAccountAddress.split('\n') ?? [];

    setAddress({
      street: streetComponents?.[0] || '',
      street2: streetComponents?.[1] || '',
      street3: streetComponents?.[2] || '',
      city: invoiceDetails?.wireToAccountCity || '',
      zip: invoiceDetails?.wireToAccountZip || '',
      name: invoiceDetails?.wireToAccountName || '',
      addressItem: undefined,
    });
  }, [invoiceDetails]);

  const handleSave = () => {
    if (invoiceDetails?.id) {
      invoicesService.updateInvoiceAddress(invoiceDetails?.id, {
        wireToAccountCity: address.city,
        wireToAccountZip: address.zip,
        wireToAccountName: address.name,
        wireToAccountAddress: [address.street, address.street2, address.street3].filter((v) => v !== undefined && v !== '').join('\n')
      }).subscribe({
        next: (clientInvoiceDetails: InvoiceDetails) => {
          handleClose?.(clientInvoiceDetails);
        },
        error: (err) => {
          enqueueSnackbar(err.text, err.options);
        }
      });
    }
  };

  return (<ModalComponent
    customColor={Colors.white}
    handleClose={handleClose}
    title={t('invoices.bills.details.updateAddress.title')}
    content={
      <Stack width="70%" spacing={2} pt="55px" pb="10px">
        <AsyncSelectComponent
          labelWidth={Sizes.drawerInputLabelWidth}
          label={t('unitDetails.contacts.address.internalName')}
          placeholder={t('global.writeSomething')}
          getOptions={() => clientDetailsService.getInvoiceAddresses(invoiceDetails?.clientId || '').pipe(
            map((options) => options.map((o) => ({
                label: o.internalName || '-',
                value: o.clientInvoiceAddressId || 0,
                data: o,
              }))
            ))}
          value={address.addressItem}
          handleChange={(value) => {
            const a = value?.data as ClientInvoiceAddress;
            setAddress({
              addressItem: value,
              street: a.street,
              street2: a.street2,
              street3: a.street3,
              city: a.city,
              zip: a.zip,
              name: a.name || ''
            });
          }}
        />
        <Divider/>
        <TextFieldComponent
          labelWidth={Sizes.drawerInputLabelWidth}
          label={t('unitDetails.contacts.address.name')}
          placeholder={t('global.writeSomething')}
          value={address?.name || ''}
          handleChange={(name) => setAddress({...address, name})}
        />
        <TextFieldComponent
          labelWidth={Sizes.drawerInputLabelWidth}
          label={t('unitDetails.contacts.address.street1')}
          placeholder={t('global.writeSomething')}
          value={address?.street || ''}
          handleChange={(street) => setAddress({...address, street})}
        />
        <TextFieldComponent
          labelWidth={Sizes.drawerInputLabelWidth}
          label={t('unitDetails.contacts.address.street2')}
          placeholder={t('global.writeSomething')}
          value={address?.street2 || ''}
          handleChange={(street2) => setAddress({...address, street2})}
        />
        <TextFieldComponent
          labelWidth={Sizes.drawerInputLabelWidth}
          label={t('unitDetails.contacts.address.street3')}
          placeholder={t('global.writeSomething')}
          handleChange={(street3) => setAddress({...address, street3})}
        />
        <TextFieldComponent
          labelWidth={Sizes.drawerInputLabelWidth}
          label={t('unitDetails.contacts.address.city')}
          placeholder={t('global.writeSomething')}
          value={address?.city || ''}
          handleChange={(city) => setAddress({...address, city})}
        />
        <TextFieldComponent
          labelWidth={Sizes.drawerInputLabelWidth}
          label={t('unitDetails.contacts.address.zip')}
          placeholder={t('global.writeSomething')}
          value={address?.zip}
          handleChange={(zip) => setAddress({...address, zip})}
        />
      </Stack>
    }
    actions={
      <>
        <Button
          variant="outlined"
          onClick={() => handleClose?.()}
          color="primary">
          {t('global.cancel')}
        </Button>
        <LoadingButton
          variant="contained"
          onClick={() => handleSave()}
          color="primary">
          {t('global.save')}
        </LoadingButton>
      </>
    }
  />);
};

export default UpdateBillAddressModal;
